// İstemci cihaz ajanları (Octo.Agent) — DAL api/device uçları. Kullanıcı JWT'siyle çağrılır;
// ajanın kendi uçları (agent/*) ve anonim eşleştirme burada yok.
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';

export interface DevicePrinterDto {
  devicePrinterId: string;
  printerName: string;
  /** Kiracıda tekil takma ad (ör. DEPO-ETIKET); @EXCPrinterName bununla eşleşir. */
  printerAlias: string | null;
  isDefault: boolean;
  /** Etiket/fiş yazıcısı: ham bayt (ZPL, ESC/POS). */
  isRawPrinter: boolean;
  active?: boolean;
  lastSeenUtc?: string | null;
}

/** COM port profili — alanlar System.IO.Ports.SerialPort adlarıyla. */
export interface DeviceComPortDto {
  deviceComPortId: string;
  comPortAlias: string;
  portName: string;
  baudRate: number;
  dataBits: number;
  parity: string;
  stopBits: string;
  handshake: string;
  dtrEnable: boolean;
  rtsEnable: boolean;
  readTimeout: number;
  writeTimeout: number;
  newLine: string;
  encoding: string;
  readMode: string;
  command: string | null;
  parsePattern: string | null;
  stableReadCount: number;
  stableWindowMs: number;
  active: boolean;
}

export interface DeviceDto {
  deviceId: string;
  deviceCode: string;
  machineName: string | null;
  description: string | null;
  active: boolean;
  online: boolean;
  lastSeenUtc: string | null;
  agentVersion: string | null;
  osVersion: string | null;
  windowsUser: string | null;
  /** Ajanın bildirdiği seri portlar (COM1, COM3…) */
  serialPortNames?: string[];
  printers: DevicePrinterDto[];
  comPortCount?: number;
  comPorts?: DeviceComPortDto[];
}

export interface PairingCodeDto {
  code: string;
  expiresUtc: string;
}

/** Sunucudaki ajan paketi; enabled=false ve url=null ise indirilecek bir şey yok. */
export interface AgentPackageDto {
  enabled: boolean;
  version: string | null;
  size: number;
  fileName: string | null;
  /** Paket klasörü yoksa dış indirme adresi (DeviceAgent:PackageUrl) */
  url: string | null;
}

export const deviceApi = {
  list: (includeInactive = false) =>
    apiClient.get<DeviceDto[]>(ENDPOINTS.DEVICES, { params: { includeInactive }, octo: { expectSuccess: false } }),

  get: (deviceId: string) => apiClient.get<DeviceDto>(ENDPOINTS.DEVICE(deviceId), { octo: { expectSuccess: false } }),

  /** Sistem rolü: 15 dk geçerli, tek kullanımlık eşleştirme kodu. */
  createPairingCode: (deviceCode?: string | null) =>
    apiClient.post<PairingCodeDto>(ENDPOINTS.DEVICE_PAIRING_CODES, { deviceCode: deviceCode ?? null }, { octo: { expectSuccess: false } }),

  update: (deviceId: string, body: { deviceCode?: string; description?: string; active?: boolean }) =>
    apiClient.put<void>(ENDPOINTS.DEVICE(deviceId), body, { octo: { expectSuccess: false } }),

  deactivate: (deviceId: string) => apiClient.delete<void>(ENDPOINTS.DEVICE(deviceId), { octo: { expectSuccess: false } }),

  updatePrinter: (devicePrinterId: string, body: { printerAlias?: string; isDefault?: boolean; isRawPrinter?: boolean }) =>
    apiClient.put<void>(ENDPOINTS.DEVICE_PRINTER(devicePrinterId), body, { octo: { expectSuccess: false } }),

  createComPort: (deviceId: string, profile: Omit<DeviceComPortDto, 'deviceComPortId'>) =>
    apiClient.post<DeviceComPortDto>(ENDPOINTS.DEVICE_COMPORTS(deviceId), profile, { octo: { expectSuccess: false } }),

  updateComPort: (deviceComPortId: string, profile: Omit<DeviceComPortDto, 'deviceComPortId'>) =>
    apiClient.put<DeviceComPortDto>(ENDPOINTS.DEVICE_COMPORT(deviceComPortId), profile, { octo: { expectSuccess: false } }),

  deleteComPort: (deviceComPortId: string) =>
    apiClient.delete<void>(ENDPOINTS.DEVICE_COMPORT(deviceComPortId), { octo: { expectSuccess: false } }),

  agentPackage: () => apiClient.get<AgentPackageDto>(ENDPOINTS.DEVICE_AGENT_PACKAGE, { octo: { expectSuccess: false } }),

  /** Ajan exe'sini indirir (sistem rolü); büyük dosya, zaman aşımı geniş. */
  downloadAgentPackage: () =>
    apiClient.getBlob(ENDPOINTS.DEVICE_AGENT_PACKAGE_DOWNLOAD, { timeout: 10 * 60_000, octo: { expectSuccess: false } }),
};
